import "./App.css";
import "./external.css";
import "./custom.css";
import "./responsive.css";
import "./responsiveCustom.css";
import "animate.css/animate.min.css";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";

import React, { memo, useEffect, useRef, useState, lazy, Suspense } from "react";
import { Route, Routes, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import SEO from "./Pages/helper/SEO";
import ApiRequest from "./Pages/helper/ApiRequest";
import { ApiEndpoints } from "./Pages/helper/ApiEndpoints";

import { NavRouter } from "./Pages/Routerpage/NavRouter";
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const serverRequire = typeof window === "undefined" ? eval("require") : null;

const isChunkLoadError = (error) => {
  const message = `${error?.name || ""} ${error?.message || ""}`;
  return /ChunkLoadError|Loading chunk|Loading CSS chunk|dynamically imported module/i.test(message);
};

const browserLazy = (loader) =>
  lazy(() =>
    loader()
      .then((module) => {
        sessionStorage.removeItem("app-chunk-reload");
        return module;
      })
      .catch((error) => {
        if (isChunkLoadError(error) && sessionStorage.getItem("app-chunk-reload") !== "1") {
          sessionStorage.setItem("app-chunk-reload", "1");
          window.location.reload();
          return new Promise(() => {});
        }

        throw error;
      })
  );

const ssrLazy = (loader, modulePath) => {
  if (typeof window !== "undefined") return browserLazy(loader);

  const loadedModule = serverRequire(modulePath);
  return loadedModule.default || loadedModule;
};

// Synchronous during SSR for complete HTML; split into route chunks in browsers.
// const TestForm = ssrLazy(() => import("./TestForm"), "./TestForm");
const QueryPopup = ssrLazy(() => import("./Pages/Query_popup/QueryPopup"), "./Pages/Query_popup/QueryPopup");
const QueryPopup2 = ssrLazy(() => import("./Pages/Query_popup/QueryPopup2"), "./Pages/Query_popup/QueryPopup2");
const LandingPage = ssrLazy(() => import("./Pages/landingPages/landingPage/LandingPage"), "./Pages/landingPages/landingPage/LandingPage");
const LandingTwo = ssrLazy(() => import("./Pages/landingPages/landingTwo/LandingTwo"), "./Pages/landingPages/landingTwo/LandingTwo");
const LandingItaly = ssrLazy(() => import("./Pages/landingPages/landingItaly/LandingItaly"), "./Pages/landingPages/landingItaly/LandingItaly");
const PolishLanding = ssrLazy(() => import("./Pages/landingPages/polishLanding/PolishLanding"), "./Pages/landingPages/polishLanding/PolishLanding");
const FrenchLanding = ssrLazy(() => import("./Pages/landingPages/frenchLanding/FrenchLanding"), "./Pages/landingPages/frenchLanding/FrenchLanding");
const SpanishLanding = ssrLazy(() => import("./Pages/landingPages/spanishLanding/SpanishLanding"), "./Pages/landingPages/spanishLanding/SpanishLanding");
const GermanLanding = ssrLazy(() => import("./Pages/landingPages/germanLanding/GermanLanding"), "./Pages/landingPages/germanLanding/GermanLanding");
const ChinaRoutes = ssrLazy(() => import("./Pages/chineseWebsite/ChinaRoutes"), "./Pages/chineseWebsite/ChinaRoutes");

const AppRouteFallback = () => (
  <div className="loader-only-css">
    <div className="loader-inner-css">
      <div></div>
    </div>
  </div>
);

const DeferredQueryPopup = () => {
  const isPopupOpen = useSelector((state) => state.popup.isPopupOpen);
  return isPopupOpen ? (
    <Suspense fallback={null}>
      <QueryPopup />
    </Suspense>
  ) : null;
};

const DeferredQueryPopup2 = () => {
  const isPopup2Open = useSelector((state) => state.popup2.isPopup2Open);
  return isPopup2Open ? (
    <Suspense fallback={null}>
      <QueryPopup2 />
    </Suspense>
  ) : null;
};

// const loadChatbotScripts = () => {
//   // Load the script dynamically
//   const script = document.createElement("script");
//   script.src = "//in.fw-cdn.com/31437866/703378.js";
//   script.setAttribute("chat", "true");
//   script.setAttribute("widgetId", "29ce8892-b5f9-4501-b7fa-9dfd898527c5");
//   document.body.appendChild(script);

//   // Configure the chatbot
//   window.fcWidgetMessengerConfig = { tags: ["b2b sales"] };
//   script.onload = () => {
//     setTimeout(() => {
//       if (window.fcWidget) window.fcWidget.open();
//     }, 10000); // 10 seconds delay
//   };
// };

function App({initialData, initialSeo, globalSchema, initialFaq}) {
  const hasInitialSeo = Boolean(initialSeo && Object.keys(initialSeo).length);
  const [seoData, setSeoData] = useState(() => hasInitialSeo ? initialSeo : null);
  const [globalSchemaData, setGlobalSchemaData] = useState(() => globalSchema || {});
  const seoByUrl = ApiEndpoints.seo.seoByUrl;
  const location = useLocation();
  const isInitialSeoEffect = useRef(true);
  const seoRequestId = useRef(0);
  const landingPageUrl = location.pathname;

  useEffect(() => {
    const hasInitialGlobalSchema = globalSchema && Object.keys(globalSchema).length > 0;
    if (hasInitialGlobalSchema || location.pathname.includes("/admin") || location.pathname.includes("/login")) {
      return;
    }

    const fetchGlobalSchema = async () => {
      try {
        const response = await ApiRequest("GET", ApiEndpoints.schema.schemaView);
        setGlobalSchemaData(response?.schema || response?.data || response || {});
      } catch (error) {
        console.error("Error fetching global schema:", error);
      }
    };

    fetchGlobalSchema();
  }, [globalSchema, location.pathname]);

  // loading chatbot script
  // useEffect(() => {
  //   loadChatbotScripts();

  //   return () => {
  //     // Cleanup: Remove the script if needed
  //     const existingScript = document.querySelector(
  //       'script[src="//in.fw-cdn.com/31437866/703378.js"]'
  //     );
  //     if (existingScript) existingScript.remove();
  //   };
  // }, []);

  // fetching seo new only one for one page
  useEffect(() => {
    const requestId = ++seoRequestId.current;
    const dataFetch = async () => {
      try {
        if (location.pathname.includes("/admin") || location.pathname.includes("/login")) {
          return
        }

        // SSR already supplied SEO for the first render only. When the user
        // later navigates back to that same URL it must be fetched again.
        if (isInitialSeoEffect.current && hasInitialSeo) {
          isInitialSeoEffect.current = false;
          return;
        }
        isInitialSeoEffect.current = false;

        // Remove the previous route's tags while the new route is loading.
        setSeoData(null);

        const response = await ApiRequest("GET", `${seoByUrl}${location.pathname}`);
        if (requestId === seoRequestId.current) {
          setSeoData(response?.seo || null);
        }
      } catch (error) {
        if (requestId === seoRequestId.current) {
          setSeoData(null);
          console.error("Error fetching SEO data:", error);
        }
      }
    };
    dataFetch();
  }, [hasInitialSeo, location.pathname, seoByUrl]);


  useEffect(() => {
    if (typeof window === "undefined") return;
    const sessionCheck = sessionStorage.getItem("sourceUrlSession");
    if (!sessionCheck) {
      const currentUrl = window.location.href;
      sessionStorage.setItem("sourceUrlSession", currentUrl);
    }
  }, []);

  // Storing referrer or current URL in sessionStorage  
  const NormalizeTrailingSlash = memo(() => {
    const location = useLocation();
    const { pathname, search, hash } = location;

    useEffect(() => {
      if (pathname.endsWith('/') && pathname !== '/') {
        const newPath = pathname.slice(0, -1) + search + hash;

        // Update the URL only if it's different
        if (newPath !== window.location.href) {
          if (typeof window !== "undefined") {
            window.history.replaceState(null, '', newPath);
          }
        }
      }
    }, [pathname, search, hash]);
    return null;
  });

  // if landing page than change routes
  if (landingPageUrl.includes("/white-label-taxi-dispatch-software-app")) {
    return (
      <>
        {/* <Suspense fallback={
          <>
            <div className="loader-only-css">
              <div className="loader-inner-css">
                <div></div>
              </div>
            </div>
          </>
        }> */}
        <div className="app">
          
            {/* <HomeBanner2 showBlackFriday={showBlackFriday}  onClose={() => setShowBlackFriday(false)}/> */}
            <SEO seoData={seoData} globalSchema={globalSchemaData} />
            {/* <DemoButton/> */}
            {/* <ChristmasPop show={modalShow} onHide={() => setModalShow(false)}/> */}
            <Routes>
              <Route path="/white-label-taxi-dispatch-software-app" element={<Suspense fallback={<AppRouteFallback />}><LandingPage /></Suspense>} />
            </Routes>
          
        </div>
        <DeferredQueryPopup />
        {/* </Suspense> */}
      </>
    );
  }

  if (landingPageUrl.includes("italy-taxi-dispatch-software-white-label")) {
    return (
      <>

        <div className="app">
          
            {/* <HomeBanner2 showBlackFriday={showBlackFriday}  onClose={() => setShowBlackFriday(false)}/> */}
            <SEO seoData={seoData} globalSchema={globalSchemaData} />
            {/* <DemoButton/> */}
            {/* <ChristmasPop show={modalShow} onHide={() => setModalShow(false)}/> */}
            <Routes>
              <Route path="/italy-taxi-dispatch-software-white-label" element={<Suspense fallback={<AppRouteFallback />}><LandingItaly /></Suspense>} />
            </Routes>
          
        </div>
        <DeferredQueryPopup />
      </>
    );
  }

  if (landingPageUrl.includes("software-de-despacho-de-taxis-spain")) {
    return (
      <>

        <div className="app">
          
            {/* <HomeBanner2 showBlackFriday={showBlackFriday}  onClose={() => setShowBlackFriday(false)}/> */}
            <SEO seoData={seoData} globalSchema={globalSchemaData} />
            {/* <DemoButton/> */}
            {/* <ChristmasPop show={modalShow} onHide={() => setModalShow(false)}/> */}
            <Routes>
              <Route path="/software-de-despacho-de-taxis-spain" element={<Suspense fallback={<AppRouteFallback />}><SpanishLanding /></Suspense>} />
            </Routes>
          
        </div>
        <DeferredQueryPopup />
      </>
    );
  }

  if (landingPageUrl.includes("germany-taxi-dispatch-software")) {
    return (
      <>

        <div className="app">
          
            {/* <HomeBanner2 showBlackFriday={showBlackFriday}  onClose={() => setShowBlackFriday(false)}/> */}
            <SEO seoData={seoData} globalSchema={globalSchemaData} />
            {/* <DemoButton/> */}
            {/* <ChristmasPop show={modalShow} onHide={() => setModalShow(false)}/> */}
            <Routes>
              <Route path="/germany-taxi-dispatch-software" element={<Suspense fallback={<AppRouteFallback />}><GermanLanding /></Suspense>} />
            </Routes>
          
        </div>
        <DeferredQueryPopup />
      </>
    );
  }

  if (landingPageUrl.includes("polska-oprogramowanie-do-dyspozytorni-taxi")) {
    return (
      <>

        <div className="app">
          
            {/* <HomeBanner2 showBlackFriday={showBlackFriday}  onClose={() => setShowBlackFriday(false)}/> */}
            <SEO seoData={seoData} globalSchema={globalSchemaData} />
            {/* <DemoButton/> */}
            {/* <ChristmasPop show={modalShow} onHide={() => setModalShow(false)}/> */}
            <Routes>
              <Route path="/polska-oprogramowanie-do-dyspozytorni-taxi" element={<Suspense fallback={<AppRouteFallback />}><PolishLanding /></Suspense>} />
            </Routes>
          
        </div>
        <DeferredQueryPopup />
      </>
    );
  }

  if (landingPageUrl.includes("logiciel-de-repartition-de-taxis-france")) {
    return (
      <>

        <div className="app">
          
            {/* <HomeBanner2 showBlackFriday={showBlackFriday}  onClose={() => setShowBlackFriday(false)}/> */}
            <SEO seoData={seoData} globalSchema={globalSchemaData} />
            {/* <DemoButton/> */}
            {/* <ChristmasPop show={modalShow} onHide={() => setModalShow(false)}/> */}
            <Routes>
              <Route path="/logiciel-de-repartition-de-taxis-france" element={<Suspense fallback={<AppRouteFallback />}><FrenchLanding /></Suspense>} />
            </Routes>
          
        </div>
        <DeferredQueryPopup />
      </>
    );
  }

  if (landingPageUrl.includes("/best-white-label-taxi-dispatch-software")) {
    return (
      <>

        <div className="app">
          
            {/* <HomeBanner2 showBlackFriday={showBlackFriday}  onClose={() => setShowBlackFriday(false)}/> */}
            <SEO seoData={seoData} globalSchema={globalSchemaData} />
            {/* <DemoButton/> */}
            {/* <ChristmasPop show={modalShow} onHide={() => setModalShow(false)}/> */}
            <Routes>
              <Route path="/best-white-label-taxi-dispatch-software" element={<Suspense fallback={<AppRouteFallback />}><LandingTwo /></Suspense>} />
            </Routes>
          
        </div>
        <DeferredQueryPopup />
      </>
    );
  }

  if (landingPageUrl.includes("/zh-tw")) {
    return (
      <>
        <div className="app">
          
            <SEO seoData={seoData} globalSchema={globalSchemaData} />
            {/* <HomeBanner2 showBlackFriday={showBlackFriday}  onClose={() => setShowBlackFriday(false)}/> */}
            {/* <ChristmasPop show={modalShow} onHide={() => setModalShow(false)}/> */}
            <NormalizeTrailingSlash />
            <Suspense fallback={<AppRouteFallback />}>
              <ChinaRoutes />
            </Suspense>
          
          <DeferredQueryPopup2 />
        </div>
      </>
    );
  }

  // if (landingPageUrl.includes("/testing-form")) {
  //   return (
  //     <>
  //       <div className="app">
  //           <Suspense fallback={<AppRouteFallback />}>
  //             <TestForm />
  //           </Suspense>
  //       </div>
  //     </>
  //   );
  // }

  // else return our full app
  return (
    <>
      <div className="app">
        
          <SEO seoData={seoData} globalSchema={globalSchemaData} />
          {/* <DemoButton/> */}
          <NormalizeTrailingSlash />
          {/* 
          {landingPageUrl.includes("/admin")? null:
          <HomeBanner2 showBlackFriday={showBlackFriday}  onClose={() => setShowBlackFriday(false)}/>} */}

          {/* {landingPageUrl.includes("/admin")? null: landingPageUrl.includes("/login")? null :
          <ChristmasPop show={modalShow} onHide={() => setModalShow(false)}/>} */}

          <NavRouter initialData={initialData} initialFaq={initialFaq}/>
          <ToastContainer />
        
        <DeferredQueryPopup />
      </div>
    </>
  );
}

export default App;
